import { Dropzone as BaseDropzone } from 'dropzone';
import Sortable from 'sortablejs';
import axios from 'axios';

export default class Dropzone {
  /**
   * @param container {HTMLElement}
   * @param url {string}
   * @param options {Object}
   */
  constructor({ container, url, options = {} }) {
    const defaultOptions = {
      paramName: 'file',
      maxFilesize: 32, // MB
      maxFiles: Number(container.dataset.maxFiles ?? 50),
      acceptedFiles: 'image/jpeg,image/png,image/webp',
      addRemoveLinks: true,
      thumbnailWidth: 60,
      thumbnailHeight: 60,
      dictRemoveFile: '',
      dictCancelUpload: '',
      parallelUploads: 1, // do not change, keeps correct order on page reload
      dictMaxFilesExceeded: container.dataset.maxExceededMessage ?? BaseDropzone.options.dictMaxFilesExceeded,
      dictInvalidFileType: container.dataset.fileDisallowedMessage ?? BaseDropzone.options.dictInvalidFileType,
    };

    this.dom = {
      container,
      previewsContainer: document.getElementById('template-preview'),
    };

    this.props = {
      url,
    };

    this.data = {
      options: { ...defaultOptions, url, ...options },
    };

    this.components = {
      dropzone: null,
      sortable: null,
    };

    this.mount();
  }

  mount() {
    if (this.dom.container) {
      this.initializeDropzone();
    }
  }

  initializeDropzone() {
    this.components.dropzone = new BaseDropzone(this.dom.container, {
      ...this.data.options,
      previewsContainer: this.dom.previewsContainer,
      previewTemplate:
        `<div class="dz-preview dz-file-preview">
          <div class="dz-image"><img data-dz-thumbnail /></div>
          <div class="dz-details">
            <div class="dz-size"><span data-dz-size></span></div>
            <div class="dz-filename"><span data-dz-name></span></div>
          </div>
          <div class="dz-progress">
            <span class="dz-upload" data-dz-uploadprogress></span>
          </div>
          <div class="dz-error-message"><span data-dz-errormessage></span></div>
          <div class="dz-success-mark">
            <svg width="54" height="54" viewBox="0 0 54 54" fill="white" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.2071 29.7929L14.2929 25.7071C14.6834 25.3166 15.3166 25.3166 15.7071 25.7071L21.2929 31.2929C21.6834 31.6834 22.3166 31.6834 22.7071 31.2929L38.2929 15.7071C38.6834 15.3166 39.3166 15.3166 39.7071 15.7071L43.7929 19.7929C44.1834 20.1834 44.1834 20.8166 43.7929 21.2071L22.7071 42.2929C22.3166 42.6834 21.6834 42.6834 21.2929 42.2929L10.2071 31.2071C9.81658 30.8166 9.81658 30.1834 10.2071 29.7929Z" />
            </svg>
          </div>
          <div class="dz-error-mark">
            <svg width="54" height="54" viewBox="0 0 54 54" fill="white" xmlns="http://www.w3.org/2000/svg">
              <path d="M26.2929 20.2929L19.2071 13.2071C18.8166 12.8166 18.1834 12.8166 17.7929 13.2071L13.2071 17.7929C12.8166 18.1834 12.8166 18.8166 13.2071 19.2071L20.2929 26.2929C20.6834 26.6834 20.6834 27.3166 20.2929 27.7071L13.2071 34.7929C12.8166 35.1834 12.8166 35.8166 13.2071 36.2071L17.7929 40.7929C18.1834 41.1834 18.8166 41.1834 19.2071 40.7929L26.2929 33.7071C26.6834 33.3166 27.3166 33.3166 27.7071 33.7071L34.7929 40.7929C35.1834 41.1834 35.8166 41.1834 36.2071 40.7929L40.7929 36.2071C41.1834 35.8166 41.1834 35.1834 40.7929 34.7929L33.7071 27.7071C33.3166 27.3166 33.3166 26.6834 33.7071 26.2929L40.7929 19.2071C41.1834 18.8166 41.1834 18.1834 40.7929 17.7929L36.2071 13.2071C35.8166 12.8166 35.1834 12.8166 34.7929 13.2071L27.7071 20.2929C27.3166 20.6834 26.6834 20.6834 26.2929 20.2929Z" />
            </svg>
          </div>
          <div class="dz-spinner"></div>
        </div>`,
      init: () => {
        this.initializeExistingFiles();
      },
      error: (file, response) => {
        if (file.previewElement) {
          // Show errors
          file.previewElement.classList.add('dz-error');
          // Parse the message
          const message = typeof response === 'string'
            ? response
            : Object.values(response?.errors ?? {}).join(', ');

          // Set the message to display
          for (const element of file.previewElement.querySelectorAll('[data-dz-errormessage]')) {
            element.textContent = message;
          }
        }
      },
    });

    // Add the necessary action
    this.components.dropzone.on('sending', (file, xhr, formData) => {
      formData.append('action', 'upload-file');
    });

    // Save the id of the file
    this.components.dropzone.on('success', (file, responseData) => {
      // eslint-disable-next-line no-param-reassign
      file.id = responseData.id;

      // Add file id attribute to the preview element
      if (file.previewElement) {
        // eslint-disable-next-line no-param-reassign
        file.previewElement.dataset.fileId = file.id;
      }
    });

    // Override remove function to delete the file the on server
    const originalRemoveFunction = this.components.dropzone.removeFile.bind(this.components.dropzone);
    this.components.dropzone.removeFile = (file) => {
      if (file.status === BaseDropzone.ERROR) {
        // Skip delete request when file has error
        originalRemoveFunction(file); // execute dropzone remove function
        return;
      }

      this.removeFile(file)
        // When successful, execute dropzone remove function
        .then(() => originalRemoveFunction(file));
    };
  }

  initializeExistingFiles() {
    axios.get(this.props.url, {
      params: {
        action: 'get-existing-files',
      },
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        const files = response?.data ?? [];

        files.forEach((file) => {
          const mockFile = {
            name: file.name,
            size: file.size,
            id: file.id,
            accepted: true,
          };

          this.components.dropzone.displayExistingFile(mockFile, file.url);

          // Add file id attribute to the preview element
          if (mockFile.previewElement) {
            mockFile.previewElement.dataset.fileId = file.id;
          }
        });

        // Update the max files
        this.setMaxFiles(this.getMaxFiles() - files.length);
        this.initializeSortable();
      });
  }

  initializeSortable() {
    this.components.sortable = new Sortable(this.dom.previewsContainer, {
      animation: 150,
      onEnd: () => {
        // Get the file ids
        const positionedFileIds = [...this.dom.previewsContainer.children]
          .filter((preview) => preview.dataset.fileId)
          .map((preview) => preview.dataset.fileId);

        const formData = new FormData();
        formData.append('action', 'position-file');
        // Add the file id in the new order
        positionedFileIds.forEach((fileId, index) => {
          formData.append(`PositionedFiles[${index}]`, fileId);
        });

        axios.post(this.props.url, formData);
      },
    });
  }

  removeFile(file) {
    // set file loading state
    file.previewElement.classList.add('dz-loading');

    // Create form data
    const formData = new FormData();
    formData.append('action', 'delete-file');
    formData.append('FileID', file.id);

    return axios.post(this.props.url, formData)
      .then(() => {
        // Increase the max files
        this.setMaxFiles(this.getMaxFiles() + 1);
      })
      .catch((error) => {
        // failed to remove file, allow file to be removed
        file.previewElement.classList.remove('dz-loading');
        throw new Error(error);
      });
  }

  /**
   * @return {number}
   */
  getMaxFiles() {
    return this.components.dropzone.options.maxFiles;
  }

  /**
   * @param value {number}
   */
  setMaxFiles(value) {
    this.components.dropzone.options.maxFiles = Math.max(0, value);
  }
}
